import { loadWrapper } from "aic";
import { HierarchyQueryParams } from "functions/api/hierarchy/HierarchyQueryParams";
import { HierarchyReadDto } from "functions/api/hierarchy/HierarchyReadDto";
import { loadHierarchyTlfTifos } from "functions/api/hierarchy/loadHierarchyTlfTifos";
import { Dispatch } from "react";
import { IRequestStatus } from "../../functions/api/interface";
import { MosaicTocEntryDto } from "./interface";
import { MosaicTocSectionClass } from "./MosaicTocSectionClass";
import { ITocAction, REFRESH } from "./tocContextReducer";

/**
 * Refresh the ToC items for the provided hierarchy by loading TLF/TiFo data
 * from the backend and dispatching a `REFRESH` action with the combined
 * list of entries and sections.
 *
 * @param tocRequest - Current request status object used for load state.
 * @param setTocRequest - Setter to update the request status during loading.
 * @param backEndUrl - Backend base URL to call the hierarchy API.
 * @param hierarchy - The hierarchy descriptor for which to load ToC items.
 * @param dispatch - Reducer dispatch to send the `REFRESH` action.
 * @param force - When true, forces a fresh load ignoring cached request id.
 * @returns The array of refreshed ToC items when successful, otherwise undefined.
 */
export const refreshToc = async (
  tocRequest: IRequestStatus<HierarchyQueryParams>,
  setTocRequest: (ret: IRequestStatus<HierarchyQueryParams>) => void,
  backEndUrl: string,
  hierarchy: HierarchyReadDto,
  dispatch: Dispatch<ITocAction>,
  force: boolean,
) => {
  if (!tocRequest.requesting) {
    // Get links
    const tocItems = await loadWrapper(
      loadHierarchyTlfTifos,
      { hierarchyId: hierarchy.id, parentTag: hierarchy.parentTag },
      { ...tocRequest, requestedId: force ? undefined : tocRequest.requestedId, error: false },
      setTocRequest,
      backEndUrl,
    );

    // Process successful return
    if (tocItems?.success) {
      const tlfItems = tocItems.data.map((e) => {
        const tlfItem: MosaicTocEntryDto = {
          ...e,
          tocNumber: e.tocNumber,
          tocLabel: e.tocLabel,
          tocOrder: e.tocOrder,
          changed: false,
          inError: false,
          changedTitles: e.changedTitles ?? [],
        };
        return tlfItem;
      });
      const sections = hierarchy.sections.map(
        (section) =>
          new MosaicTocSectionClass({
            primaryHierarchyId: hierarchy.id,
            ...section,
          }),
      );
      const returnedItems = [...tlfItems, ...sections].sort(
        (a, b) =>
          (a.tocOrder ??
            // istanbul ignore next
            0) -
          (b.tocOrder ??
            // istanbul ignore next
            0),
      );
      dispatch({
        operation: REFRESH,
        hierarchyId: hierarchy.id,
        items: returnedItems,
      });
      return returnedItems;
    }
  }
};
